/**
 * /flpinfo — single-file project inspector.
 *
 * Drop one .flp file, get every detail dawhub can extract: project
 * metadata, channel / pattern / mixer / arrangement counts, sample paths,
 * and — the diagnostic feature this page exists for — full plugin info
 * for every VST instance in the project (display name, vendor, FourCC,
 * absolute path on disk). Useful when a plugin file got corrupted and
 * you need to identify the exact edition / version that was loaded.
 *
 * Local state — does not touch the diff viewer's global state machine.
 */

import { parseFLPFile, type FLPProject, FLPParseError } from "flpdiff";
import { renderProjectInfo } from "./render-info.ts";
import { h, empty } from "./ui.ts";
import { renderNav, renderFooter } from "./page-shared.ts";

interface LocalState {
  file: { name: string; bytes: number; project: FLPProject } | null;
  error: { name?: string; message: string } | null;
}

let state: LocalState = { file: null, error: null };

export function renderFlpinfoPage(): HTMLElement {
  return h("div", { class: "flpinfo-page" }, [
    renderNav("flpinfo"),
    renderHero(),
    renderInspector(),
    renderFooter(),
  ]);
}

function renderHero(): HTMLElement {
  return h("section", { class: "fp-hero fi-hero" },
    h("div", { class: "container" }, [
      h("div", { class: "fp-hero__copy" }, [
        h("span", { class: "hero-eyebrow" }, "DIAGNOSTIC TOOL"),
        h("h1", { class: "fi-hero__title" }, "FLP project info"),
        h("p", { class: "fp-hero__sub" }, "Drop one FL Studio project file. Get every detail dawhub can extract — including the exact path of every VST that was loaded. Files parse locally, nothing uploads."),
        h("p", { class: "fi-hero__usecase" }, [
          h("strong", null, "Lost or corrupted a plugin? "),
          "This page reads the project's plugin records (display name, vendor, FourCC, path on disk) so you can re-download the exact edition the project expects.",
        ]),
      ]),
    ]),
  );
}

function renderInspector(): HTMLElement {
  return h("section", { class: "fi-inspector", id: "inspector" },
    h("div", { class: "container fi-shell" }, [
      h("section", { id: "fi-dropzone-slot", class: "dropzone-slot" }),
      h("section", { id: "fi-error-slot", class: "error-slot", "aria-live": "polite" }),
      h("section", { id: "fi-info-slot", class: "info-slot", "aria-live": "polite" }),
      h("section", { id: "fi-plugins-slot", class: "fi-plugins-slot", "aria-live": "polite" }),
    ]),
  );
}

/**
 * Mount dynamic content for /flpinfo. main.ts calls this after appending
 * `renderFlpinfoPage()` to the DOM and on every state change.
 */
export function paintFlpinfo(): void {
  paintDropzone();
  paintError();
  paintInfo();
  paintPlugins();
}

function setState(next: Partial<LocalState>): void {
  state = { ...state, ...next };
  paintFlpinfo();
}

function resetFile(): void {
  setState({ file: null, error: null });
}

async function loadFile(file: File): Promise<void> {
  if (!file.name.toLowerCase().endsWith(".flp")) {
    setState({ error: { name: file.name, message: "Only .flp files are supported" } });
    return;
  }
  try {
    const buf = await file.arrayBuffer();
    const project = parseFLPFile(buf);
    setState({ file: { name: file.name, bytes: file.size, project }, error: null });
  } catch (e) {
    const name = e instanceof FLPParseError ? "FLPParseError" : "ParseError";
    const message = e instanceof Error ? e.message : String(e);
    setState({ error: { name, message } });
  }
}

// ---- paint helpers --------------------------------------------------

function paintDropzone(): void {
  const slot = document.querySelector("#fi-dropzone-slot");
  if (!slot) return;
  empty(slot as HTMLElement);

  if (state.file) {
    const reset = h("button", {
      type: "button",
      class: "demo-link",
      onclick: resetFile,
    }, "Inspect another file");
    slot.appendChild(h("div", { class: "fi-toolbar" }, [reset]));
    return;
  }

  const input = h("input", {
    type: "file",
    accept: ".flp",
    multiple: false,
    class: "file-input",
    id: "fi-file-input",
    "aria-label": "Choose a .flp file to inspect",
  });
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    if (f) void loadFile(f);
    input.value = "";
  });

  const label = h("label", { for: "fi-file-input", class: "dropzone dropzone--big" }, [
    h("p", { class: "dropzone__title" }, "Drop one .flp file"),
    h("p", { class: "dropzone__hint" }, "or click to browse"),
  ]);

  const wrap = h("div", { class: "dropzone-wrap" }, [label, input, renderPrivacyNote()]);
  wireLocalDrag(wrap);
  slot.appendChild(wrap);
}

function wireLocalDrag(target: HTMLElement): void {
  let depth = 0;
  const set = (on: boolean) => target.toggleAttribute("data-drag-active", on);
  target.addEventListener("dragenter", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth++;
    set(true);
  });
  target.addEventListener("dragover", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
  });
  target.addEventListener("dragleave", (e) => {
    if (!hasFiles(e)) return;
    depth = Math.max(0, depth - 1);
    if (depth === 0) set(false);
  });
  target.addEventListener("drop", (e) => {
    e.preventDefault();
    depth = 0;
    set(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) void loadFile(f);
  });
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
}

function renderPrivacyNote(): HTMLElement {
  const note = h("p", { class: "privacy-note" });
  note.innerHTML =
    '<svg class="privacy-note__icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">' +
    '<path d="M5 7V5a3 3 0 0 1 6 0v2h.5a1 1 0 0 1 1 1v5.5a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1H5Zm1 0h4V5a2 2 0 1 0-4 0v2Z" fill="currentColor"/>' +
    "</svg>" +
    "<span>Files are parsed in your browser. Nothing is uploaded.</span>";
  return note;
}

function paintError(): void {
  const slot = document.querySelector("#fi-error-slot");
  if (!slot) return;
  empty(slot as HTMLElement);
  if (!state.error) return;
  const close = h("button", {
    type: "button",
    class: "error__close",
    "aria-label": "Dismiss",
    onclick: () => setState({ error: null }),
  }, "×");
  const box = h("div", { class: "error", role: "alert" }, [
    h("span", { class: "error__icon", "aria-hidden": "true" }, "!"),
    h("span", { class: "error__msg" }, [
      state.error.name ? h("strong", null, `${state.error.name}: `) : null,
      state.error.message,
    ]),
    close,
  ]);
  slot.appendChild(box);
}

function paintInfo(): void {
  const slot = document.querySelector("#fi-info-slot");
  if (!slot) return;
  empty(slot as HTMLElement);
  if (!state.file) return;
  slot.appendChild(
    renderProjectInfo(state.file.project, state.file.name, state.file.bytes, "project"),
  );
}

// ---- plugin diagnostic ---------------------------------------------

interface PluginRecord {
  channelOrSlot: "channel" | "mixer slot";
  hostName: string;
  internalName: string;
  vstName?: string;
  vendor?: string;
  pluginPath?: string;
  /** VST2 4-char identifier (e.g. `XfrX` for Serum). Undefined for VST3. */
  fourCC?: string;
  /** VST3 16-byte class id, hex. Undefined for VST2. */
  guid?: string;
}

function renderPluginId(r: PluginRecord): HTMLElement | string {
  if (r.fourCC) return h("code", { class: "fi-plugins__fourcc-code" }, r.fourCC);
  if (r.guid) return h("code", { class: "fi-plugins__guid", title: r.guid }, r.guid);
  return "—";
}

function paintPlugins(): void {
  const slot = document.querySelector("#fi-plugins-slot");
  if (!slot) return;
  empty(slot as HTMLElement);
  if (!state.file) return;

  const records = collectPlugins(state.file.project);

  const header = h("header", { class: "fi-section__head" }, [
    h("h2", null, "Plugins in this project"),
    h("span", { class: "fi-section__count" }, String(records.length)),
  ]);

  if (records.length === 0) {
    slot.appendChild(h("section", { class: "fi-section" }, [
      header,
      h("p", { class: "fi-section__empty" }, "No hosted plugins detected (project may be sample-only)."),
    ]));
    return;
  }

  const table = h("table", { class: "fp-table fi-plugins" }, [
    h("thead", null, h("tr", null, [
      h("th", null, "Where"),
      h("th", null, "Display name"),
      h("th", null, "Vendor"),
      h("th", null, "Plugin ID"),
      h("th", null, "Path on disk"),
    ])),
    h("tbody", null,
      records.map((r) => h("tr", null, [
        h("td", null, [
          h("span", { class: "fi-plugins__where" }, r.channelOrSlot),
          h("br"),
          h("span", { class: "fi-plugins__host" }, r.hostName),
        ]),
        h("td", null, r.vstName ?? r.internalName),
        h("td", { class: "fi-plugins__vendor" }, r.vendor ?? "—"),
        h("td", { class: "fi-plugins__fourcc" }, renderPluginId(r)),
        h("td", { class: "fi-plugins__path" }, r.pluginPath
          ? h("code", null, r.pluginPath)
          : "—"),
      ])),
    ),
  ]);

  slot.appendChild(h("section", { class: "fi-section" }, [
    header,
    h("p", { class: "fi-section__hint" }, [
      "If a plugin won't load after reinstalling, match the entry's ",
      h("strong", null, "vendor + display name + Plugin ID"),
      " against the version you re-download. ",
      h("strong", null, "Plugin ID"),
      " is the VST2 4-char FourCC for VST2 plugins or the 16-byte VST3 class GUID for VST3 — both uniquely identify the plugin class. The ",
      h("strong", null, "path on disk"),
      " shows where FL Studio expected the binary at the last save.",
    ]),
    h("p", { class: "fi-section__hint fi-section__hint--note" }, [
      h("strong", null, "Note: "),
      "Plugin ", h("em", null, "version"),
      " is not stored in the .flp file — FL only records the wrapper info above. " +
      "To reinstall the matching version, check the plugin developer's release history " +
      "or use the same date/installer you had before.",
    ]),
    h("div", { class: "fp-table-wrap" }, table),
  ]));
}

function collectPlugins(project: FLPProject): PluginRecord[] {
  const records: PluginRecord[] = [];

  for (const ch of project.channels) {
    const p = ch.plugin;
    if (!p) continue;
    const hostName = ch.name ?? `Channel iid ${ch.iid}`;
    records.push({
      channelOrSlot: "channel",
      hostName,
      internalName: p.internalName,
      vstName: p.name,
      vendor: p.vendor,
    });
  }

  for (const ins of project.inserts) {
    for (const s of ins.slots) {
      if (!s.hasPlugin && !s.pluginName && !s.internalName) continue;
      const insertLabel = ins.name
        ? `Insert ${ins.index} "${ins.name}" · slot ${s.index + 1}`
        : `Insert ${ins.index} · slot ${s.index + 1}`;
      records.push({
        channelOrSlot: "mixer slot",
        hostName: insertLabel,
        internalName: s.internalName ?? s.pluginName ?? "(unknown)",
        vstName: s.pluginVstName ?? s.pluginName,
        vendor: s.pluginVendor,
      });
    }
  }

  enrichWithVstWrapperPaths(project, records);

  return records;
}

const OP_NEW_CHANNEL = 0x40;
const OP_INSERT_END = 0x93; // closes the current insert
const OP_INSERT_FLAGS = 0xec; // first occurrence marks entry into mixer scope
const OP_SLOT_BEGIN = 0x62;
const OP_PLUGIN_STATE = 0xd5;
const OP_INTERNAL_NAME_C9 = 0xc9;

const VST_ID_FOURCC = 51;
const VST_ID_GUID = 52;
const VST_ID_NAME = 54;
const VST_ID_PATH = 55;
const VST_ID_VENDOR = 56;

/**
 * Walk events and pair each plugin-state blob with its channel- or slot-
 * context, decoding the VST wrapper record stream inline. The model
 * already carries display name + vendor; this pass enriches with
 * pluginPath and FourCC, which the upstream model doesn't surface.
 *
 * Scope tracking mirrors the upstream parser:
 *   0x40 (u16) — new channel; resets to channel scope
 *   0xEC (first occurrence) — entered mixer scope; master implicit (idx 0)
 *   0x93        — closes current insert; insertCursor++
 *   0x62        — new slot in current insert
 *   0xC9 (blob) — slot internal name (e.g. "Fruity Wrapper")
 *   0xD5 (blob) — plugin state; decode as VST wrapper when applicable
 */
function enrichWithVstWrapperPaths(project: FLPProject, records: PluginRecord[]): void {
  let channelCursor = -1;
  let insertCursor = -1;
  let slotCursor = -1;
  let inMixer = false;
  let pendingSlotInternalName: string | undefined;

  const slotRecMap = new Map<string, number>();
  for (let i = 0; i < records.length; i++) {
    const r = records[i]!;
    if (r.channelOrSlot !== "mixer slot") continue;
    const m = /Insert\s+(\d+).*slot\s+(\d+)/.exec(r.hostName);
    if (m) {
      const ins = Number(m[1]);
      const slot = Number(m[2]) - 1;
      slotRecMap.set(`${ins}:${slot}`, i);
    }
  }

  for (const ev of project.events) {
    if (ev.opcode === OP_NEW_CHANNEL && ev.kind === "u16") {
      channelCursor++;
      inMixer = false;
      insertCursor = -1;
      slotCursor = -1;
      pendingSlotInternalName = undefined;
      continue;
    }

    if (ev.opcode === OP_INSERT_FLAGS && !inMixer) {
      inMixer = true;
      insertCursor = 0;
      slotCursor = -1;
      pendingSlotInternalName = undefined;
      continue;
    }

    if (ev.opcode === OP_INSERT_END) {
      if (!inMixer) {
        inMixer = true;
        insertCursor = 0;
      }
      insertCursor++;
      slotCursor = -1;
      pendingSlotInternalName = undefined;
      continue;
    }

    if (ev.opcode === OP_SLOT_BEGIN && ev.kind === "u16") {
      // 0x62's `value` IS the slot index. FL emits one 0x62 per slot
      // (filled or empty); slot N's events appear between 0x62 value=N
      // and the next 0x62. Slot 0 is the only special case: when slot 0
      // has events, FL omits 0x62 value=0 before them — those events
      // get caught by the implicit slotCursor=0 promotion below.
      slotCursor = ev.value;
      pendingSlotInternalName = undefined;
      continue;
    }

    if (ev.opcode === OP_INTERNAL_NAME_C9 && ev.kind === "blob" && inMixer) {
      // FL omits 0x62 for slot 0 on inserts whose first plugin lives
      // in slot 0 — promote slotCursor implicitly so we capture the
      // wrapper info for that slot.
      if (slotCursor < 0) slotCursor = 0;
      try {
        pendingSlotInternalName = decodeFlText(
          (ev as { payload: Uint8Array }).payload,
        );
      } catch {
        /* swallow */
      }
      continue;
    }

    if (ev.opcode === OP_PLUGIN_STATE && ev.kind === "blob") {
      const payload = (ev as { payload: Uint8Array }).payload;
      if (!inMixer && channelCursor >= 0) {
        const wrapper = decodeVSTWrapperLocal(payload);
        if (wrapper) {
          const rec = findChannelRecord(records, project, channelCursor);
          if (rec) {
            if (wrapper.pluginPath) rec.pluginPath = wrapper.pluginPath;
            if (wrapper.fourCC) rec.fourCC = wrapper.fourCC;
            if (wrapper.guid) rec.guid = wrapper.guid;
            if (!rec.vendor && wrapper.vendor) rec.vendor = wrapper.vendor;
            if (!rec.vstName && wrapper.name) rec.vstName = wrapper.name;
          }
        }
        continue;
      }
      if (inMixer) {
        if (slotCursor < 0) slotCursor = 0;
        if (pendingSlotInternalName !== "Fruity Wrapper") continue;
        const wrapper = decodeVSTWrapperLocal(payload);
        if (!wrapper) continue;
        const idx = slotRecMap.get(`${insertCursor}:${slotCursor}`);
        if (idx === undefined) continue;
        const rec = records[idx]!;
        if (wrapper.pluginPath) rec.pluginPath = wrapper.pluginPath;
        if (wrapper.fourCC) rec.fourCC = wrapper.fourCC;
        if (wrapper.guid) rec.guid = wrapper.guid;
        if (!rec.vendor && wrapper.vendor) rec.vendor = wrapper.vendor;
        if (!rec.vstName && wrapper.name) rec.vstName = wrapper.name;
      }
    }
  }
}

function findChannelRecord(
  records: PluginRecord[],
  project: FLPProject,
  channelCursor: number,
): PluginRecord | undefined {
  let idx = 0;
  for (let i = 0; i <= channelCursor && i < project.channels.length; i++) {
    const ch = project.channels[i]!;
    if (!ch.plugin) continue;
    if (i === channelCursor) {
      return records[idx];
    }
    idx++;
  }
  return undefined;
}

interface VSTWrapperLocal {
  name?: string;
  vendor?: string;
  pluginPath?: string;
  fourCC?: string;
  /** 16-byte VST3 class id, hex-formatted as 32 lowercase chars. */
  guid?: string;
}

function decodeVSTWrapperLocal(payload: Uint8Array): VSTWrapperLocal | null {
  if (payload.byteLength < 4) return null;
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const utf8 = new TextDecoder("utf-8", { fatal: false });
  const info: VSTWrapperLocal = {};
  let pos = 4;
  const HDR = 12;
  while (pos + HDR <= payload.byteLength) {
    const id = view.getUint32(pos, true);
    const len64 = view.getBigUint64(pos + 4, true);
    if (len64 > BigInt(payload.byteLength - pos - HDR)) break;
    const len = Number(len64);
    const data = payload.subarray(pos + HDR, pos + HDR + len);
    if (id === VST_ID_NAME) info.name = utf8.decode(data);
    else if (id === VST_ID_VENDOR) info.vendor = utf8.decode(data);
    else if (id === VST_ID_PATH) info.pluginPath = utf8.decode(data);
    else if (id === VST_ID_FOURCC) info.fourCC = utf8.decode(data);
    else if (id === VST_ID_GUID && data.byteLength === 16) {
      info.guid = Array.from(data, (b) => b.toString(16).padStart(2, "0")).join("");
    }
    pos += HDR + len;
  }
  if (!info.name && !info.vendor && !info.pluginPath && !info.fourCC && !info.guid) return null;
  return info;
}

function decodeFlText(payload: Uint8Array): string {
  // Detect UTF-16LE by byte 1 (0 for ASCII text encoded as UTF-16LE).
  // Decode the full payload (stripping bytes before UTF-16LE decode
  // breaks word alignment), then trim trailing NULs from the string.
  const looksUtf16Pre = payload.byteLength >= 2 && payload[1] === 0;
  if (looksUtf16Pre) {
    return new TextDecoder("utf-16le").decode(payload).replace(/\0+$/, "").trim();
  }
  let end = payload.byteLength;
  while (end > 0 && payload[end - 1] === 0) end--;
  const slice = payload.subarray(0, end);
  // FL 12+ uses UTF-8; pre-FL-12 used UTF-16LE. Detect UTF-16LE by
  // looking for interleaved NUL bytes (every odd byte is zero on
  // ASCII-only UTF-16LE strings).
  const looksUtf16 = slice.byteLength >= 2 && slice.byteLength % 2 === 0
    && slice[1] === 0 && slice[3] === 0;
  if (looksUtf16) {
    return new TextDecoder("utf-16le").decode(slice).replace(/ +$/g, "").trim();
  }
  return new TextDecoder("utf-8", { fatal: false }).decode(slice).trim();
}
